import { useEffect, useMemo, useState } from "react";

const ThemeRowContainer = (props) => {
  const theme = props.theme;
  const containerClassname = props.classname
  const getFunctionText = props.getFunctionText
  const tmEnabled = props.tmEnabled
  const tlEnabled = props.tlEnabled
  const stEnabled = props.stEnabled
  const t_idx= props.t_idx
  const surveyData = props.surveyData
  const cardType = props.cardType
  const getContainerClassname = props.getContainerClassname
  const teamMembers = props.teamMembers
  const teamLeaders = props.teamLeaders
  const stakeholders= props.stakeholders
  const generatingReport = props.generatingReport
  const origin = props.origin
  const teamMean = props.teamMean
  const teamVariance = props.teamVariance

  const [showDescription, setShowDescription] = useState(false)

  const handleHover = (bool) => {
    setShowDescription(bool)
  }

  const safeRound = (v) => (typeof v === "number" ? Math.round(v) : "N/A");

  // support flat or nested questions on theme; otherwise fall back to pillar AQS by theme
  const asScoresObj = (q) =>
    q?.scores
      ? q
      : {
          ...q,
          scores: {
            team_member_scores: q.team_member_scores || [],
            team_leader_scores: q.team_leader_scores || [],
            stakeholder_scores: q.stakeholder_scores || [],
          },
        };

  const pillarIdx = useMemo(() => {
    if (origin === "chart") return cardType; // cardType is pillar index from parent
    if (theme?.pillar_id != null) return theme.pillar_id - 1;
    return null;
  }, [origin, cardType, theme?.pillar_id]);

  const normaliseThemeQuestions = (t) => {
    if (!t) return [];
    const groups = t.topics || t.subthemes || t.subThemes;
    if (Array.isArray(groups) && groups.length) {
      return groups.map((g, gi) => ({
        groupName: g.name || g.title || `Group ${gi + 1}`,
        questions: (g.questions || []).map(asScoresObj),
      }));
    }
    if (Array.isArray(t.questions) && t.questions.length) {
      return [{ groupName: null, questions: t.questions.map(asScoresObj) }];
    }
    return [];
  };

  const questionsFromPillar = useMemo(() => {
    if (origin !== "chart") return [];
    const all = surveyData?.pillars?.[pillarIdx]?.all_question_scores || [];
    const filtered = all
      .filter((q) => q.theme_name === theme?.name)
      .map(asScoresObj);
    return filtered.length ? [{ groupName: null, questions: filtered }] : [];
  }, [origin, surveyData, pillarIdx, theme?.name]);

  const groupsToRender = useMemo(() => {
    const byTheme = normaliseThemeQuestions(theme);
    return byTheme.length ? byTheme : questionsFromPillar;
  }, [theme, questionsFromPillar]);

  useEffect(() => {
    // quick sanity log
    // console.log("TRC origin:", origin, "theme:", theme?.name, "groups:", groupsToRender);
  }, [origin, theme?.name, groupsToRender]);

  return (
    <>
      {/* THEME HEADER ROW */}
      <div className="summary-questions-theme-row" key={t_idx}>
        <div className={`summary-questions-theme-name header ${containerClassname}`}>
          {theme.name}
          <div
            className="summary-questions-theme-icon-container link"
            style={{ display: generatingReport ? "none" : null }}
            onMouseOver={() => handleHover(true)}
            onMouseOut={() => handleHover(false)}
          >
            <div>i</div>
          </div>
          {showDescription ? (
            <div className="summary-theme-empty-description-container">
              <div className={`summary-theme-description-container ${containerClassname}`}>
                {theme.description}
              </div>
            </div>
          ) : null}
        </div>

          {/* Theme summary values */}
          <div className="summary-questions-theme-values">
            {tmEnabled && (theme.team_member_scores || []).map((s, i) => (
                <div className="summary-questions-theme-score bold" key={`tm-sum-${i}`}>
                  {safeRound(s.score)}
                </div>
            ))}
            {tlEnabled && (theme.team_leader_scores || []).map((s, i) => (
                <div className="summary-questions-theme-score bold" key={`tl-sum-${i}`}>
                  {safeRound(s.score)}
                </div>
            ))}
            {stEnabled && (theme.stakeholder_scores || []).map((s, i) => (
                <div className="summary-questions-theme-score bold" key={`st-sum-${i}`}>
                  {safeRound(s.score)}
                </div>
            ))}
            <div className="summary-questions-theme-score bold">{teamMean}</div>

            <div className="summary-questions-theme-score bold">{teamVariance ?? "N/A"}</div>


            <div className="summary-questions-theme-score function bold">
              {getFunctionText(
                  theme.team_member_scores,
                  theme.team_leader_scores,
                  theme.stakeholder_scores
              )}
            </div>
          </div>
          <div className="summary-questions-heading-end-spacer" />
        </div>

        {/* Question rows (support nested sub-themes) */}
        {groupsToRender.map((group, gi) => {
          const qs = group.questions || [];
          if (!qs.length) return null;

          return (
              <div key={`grp-${gi}`}>
                {group.groupName ? (
                    <div className="summary-questions-theme-row question subtheme">
                      <div className={`summary-questions-theme-name ${getContainerClassname()}`}>
                        {group.groupName}
                      </div>
                      {/* empty value strip just to visually separate */}
                      <div className="summary-questions-theme-values" />
                      <div className="summary-questions-heading-end-spacer" />
                    </div>
                ) : null}

                {qs.map((rawQ, qi) => {
                  const q = asScoresObj(rawQ);
                  // Hide rows that are ONLY stakeholders (matches your previous rule)
                  const tmCount = q.scores.team_member_scores?.length || 0;
                  const tlCount = q.scores.team_leader_scores?.length || 0;
                  if (tmCount === 0 && tlCount === 0) return null;

                  const label = q.question || q.text || `Question ${qi + 1}`;

                  return (
                      <div className="summary-questions-theme-row question" key={`q-${gi}-${qi}`}>
                        <div className={`summary-questions-theme-name ${getContainerClassname()}`}>
                          {label}
                        </div>

                        <div className="summary-questions-theme-values">
                          {tmEnabled &&
                              (q.scores.team_member_scores || []).map((s, i) => (
                                  <div className="summary-questions-theme-score bold" key={`tm-q-${gi}-${qi}-${i}`}>
                                    {safeRound(s.score)}
                                  </div>
                              ))}

                          {tlEnabled &&
                              (q.scores.team_leader_scores || []).map((s, i) => (
                                  <div className="summary-questions-theme-score bold" key={`tl-q-${gi}-${qi}-${i}`}>
                                    {safeRound(s.score)}
                                  </div>
                              ))}

                          {/* Stakeholder per-question columns intentionally blank */}
                          {stEnabled &&
                              (theme.stakeholder_scores || stakeholders).map((s, i) => (
                                  <div className="summary-questions-theme-score hide-score bold" key={`tl-q-${gi}-${qi}-${i}`}>
                                    {/*safeRound(s.score)*/}
                                  </div>
                              ))}

                          <div className="summary-questions-theme-score bold">
                            {teamMean}
                          </div>

                          <div className="summary-questions-theme-score bold">{teamVariance ?? "N/A"}</div>

                          <div className="summary-questions-theme-score function bold">
                            {getFunctionText(
                                q.scores.team_member_scores,
                                q.scores.team_leader_scores,
                                q.scores.stakeholder_scores
                            )}
                          </div>
                        </div>

                        <div className="summary-questions-heading-end-spacer" />
                      </div>
                  );
                })}
              </div>
          );
        })}

      {/* SPACER ROW (Mean + Variance + Function) */}
      {t_idx !== (surveyData?.pillars?.[theme?.pillar_id - 1]?.themes?.length ?? 0) - 1 ? (
        <div className="summary-questions-theme-spacer-row">
          <div className="summary-questions-theme-spacer-row-wide-spacer" />
          <div className="summary-questions-theme-spacer-row-colour-container">
            {tmEnabled &&
              teamMembers.map((_, i) => (
                <div className="summary-questions-participant-heading" key={`tm-sep-${i}`}>
                  <div className="summary-questions-participant-color tm-green" />
                </div>
              ))}
            {tlEnabled &&
              teamLeaders.map((_, i) => (
                <div className="summary-questions-participant-heading" key={`tl-sep-${i}`}>
                  <div className="summary-questions-participant-color tl-pink" />
                </div>
              ))}
            {stEnabled &&
              stakeholders.map((_, i) => (
                <div className="summary-questions-participant-heading" key={`st-sep-${i}`}>
                  <div className="summary-questions-participant-color st-blue" />
                </div>
              ))}
            <div className="summary-questions-participant-heading">
              <div className="summary-questions-participant-color fill-grey" />
            </div>
            <div className="summary-questions-participant-heading">
              <div className="summary-questions-participant-color fill-grey" />
            </div>
            <div className="summary-questions-participant-heading function">
              <div className="summary-questions-participant-color fill-grey" />
            </div>
          </div>
          <div className="summary-questions-theme-spacer-row-thin-spacer" />
        </div>
      ) : null}
    </>
  );
};

export default ThemeRowContainer;
